/**
 * The generic image-download job, as the bulk-import screen drives it.
 *
 * The operator pastes merchant JSON (Shopify, WooCommerce, an extractor result
 * — anything with image fields), the admin hands it to the extractor's generic
 * image downloader through the same callable bridge brand extractors use, and
 * the rewritten JSON comes back with every image it could fetch pointing at
 * `extractor-jobs/{jobId}/images/…` in Storage. The normal bulk import then
 * promotes those to each reference's own folder on commit.
 *
 * Pure helpers only, `import type` only — the callable and the subscription
 * live in `extractorService.ts`, because importing `./firebase` into a tested
 * module drags in a browser-only decoder.
 */

import type { ExtractorJobDoc } from './extractorJobs';

/** Mirrors `IMAGE_DOWNLOADER_EXTRACTOR_ID` in functions/src/extractorHttp.ts. */
export const IMAGE_DOWNLOADER_EXTRACTOR_ID = 'generic-image-downloader';

/**
 * Mirrors `MAX_IMAGE_DOWNLOAD_INPUT_BYTES` server-side. Checked here too so an
 * oversized paste fails before it is uploaded through the callable.
 */
export const MAX_IMAGE_DOWNLOAD_INPUT_BYTES = 6 * 1024 * 1024;

export type PasteParse =
  | { ok: true; value: unknown; rows: number; bytes: number }
  | { ok: false; error: string };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Rows by the downloader's own rule: `watches[]`, the array, or the one object. */
export function countPasteRows(doc: unknown): number {
  if (isObject(doc) && Array.isArray(doc['watches'])) return doc['watches'].filter(isObject).length;
  if (Array.isArray(doc)) return doc.filter(isObject).length;
  return isObject(doc) ? 1 : 0;
}

/** Validate pasted text before it goes anywhere. */
export function parsePastedJson(text: string): PasteParse {
  if (text.trim() === '') return { ok: false, error: 'Paste some JSON first.' };
  const bytes = new TextEncoder().encode(text).length;
  if (bytes > MAX_IMAGE_DOWNLOAD_INPUT_BYTES) {
    return {
      ok: false,
      error: `The pasted JSON is ${(bytes / 1024 / 1024).toFixed(1)} MB; the limit is ${
        MAX_IMAGE_DOWNLOAD_INPUT_BYTES / 1024 / 1024
      } MB. Split it and run each half.`,
    };
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (err) {
    return { ok: false, error: `Not valid JSON — ${err instanceof Error ? err.message : String(err)}` };
  }
  const rows = countPasteRows(value);
  if (rows === 0) {
    return {
      ok: false,
      error: 'No rows found — expected an array of products or an extractor result with watches[].',
    };
  }
  return { ok: true, value, rows, bytes };
}

export interface ImageDownloadFailure {
  sourceUrl: string;
  reason: string;
  httpStatus?: number;
}

export interface ImageDownloadReport {
  rows: number;
  found: number;
  stored: number;
  reused: number;
  failed: number;
  alreadyHosted: number;
  storagePrefix: string | null;
  /** The first failures the extractor recorded on the doc (it caps the list). */
  failures: ImageDownloadFailure[];
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** `ImageFailure[]` (#122's shape), skipping anything malformed. */
function readFailures(raw: unknown): ImageDownloadFailure[] {
  if (!Array.isArray(raw)) return [];
  const failures: ImageDownloadFailure[] = [];
  for (const item of raw) {
    if (!isObject(item) || typeof item['sourceUrl'] !== 'string') continue;
    failures.push({
      sourceUrl: item['sourceUrl'],
      reason: typeof item['reason'] === 'string' ? item['reason'] : 'unknown',
      ...(typeof item['httpStatus'] === 'number' ? { httpStatus: item['httpStatus'] } : {}),
    });
  }
  return failures;
}

/**
 * The downloader's report off the job doc, read leniently: the doc is written
 * by another service, and a partial or older one must render, not throw.
 * Null when there is no report at all (still running, or an extractor build
 * that does not write one).
 */
export function readImageDownloadReport(job: ExtractorJobDoc | null | undefined): ImageDownloadReport | null {
  if (!job || !isObject(job.imageDownload)) return null;
  const download = job.imageDownload;
  const summary = isObject(download['summary']) ? download['summary'] : {};

  const failures = readFailures(job.imageFailures);

  const failed = Math.max(num(summary['failed']), num(job.imageFailureCount), failures.length);
  return {
    rows: num(summary['rows']),
    found: num(summary['found']),
    stored: num(summary['stored']),
    reused: num(summary['reused']),
    failed,
    alreadyHosted: num(summary['alreadyHosted']),
    storagePrefix: typeof download['storagePrefix'] === 'string' ? download['storagePrefix'] : null,
    failures,
  };
}

/**
 * The failure list for a rewritten ENVELOPE, which carries #123's inline
 * `_imageFailures`. Used when the doc's own list is missing or capped shorter.
 */
export function inlineImageFailures(output: unknown): ImageDownloadFailure[] {
  return isObject(output) ? readFailures(output['_imageFailures']) : [];
}

export type ImageDownloadPhase = 'starting' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

/** Where the card is. A doc that has not arrived yet is still starting. */
export function imageDownloadPhase(job: ExtractorJobDoc | null | undefined): ImageDownloadPhase {
  return job?.status ?? 'starting';
}

/** One line for the card's header. */
export function imageDownloadSummaryLine(report: ImageDownloadReport): string {
  const parts = [`${report.stored} stored`];
  if (report.reused > 0) parts.push(`${report.reused} reused`);
  if (report.alreadyHosted > 0) parts.push(`${report.alreadyHosted} already in Storage`);
  parts.push(`${report.failed} failed`);
  return `${report.found} image${report.found === 1 ? '' : 's'} across ${report.rows} row${
    report.rows === 1 ? '' : 's'
  }: ${parts.join(', ')}`;
}
